import Api from '@/Api';
import { RequestParamsMissingError } from './Errors';

export default class BillingApi extends Api {
  /**
   * Fetches payment history for the group/subgroup in context with additional supported filters
   * @param {Object}   [options={}] - configuration for the API request
   * @param {Object}   options.params - the query parameters of the request
   * @param {String}   options.params.startDate - start date for the query
   * @param {String}   options.params.endDate - end date for the query
   * @param {Number}   options.params.groupCk - group context for the query
   * @param {Number}   options.params.subgroupCk - subgroup context for the query
   * @param {Number}   options.params.id - the invoice number for the query
   */
  getPaymentHistory = options => {
    console.log('getPaymentHistory', options);
    return this.get('/api/v1/private/payments/history', options.params);
  };

  /**
   * Fetches Invoice payment history for the group/subgroup in context with additional supported filters
   * @param {Object}   [options={}] - configuration for the API request
   * @param {Object}   options.params - the query parameters of the request
   * @param {Number}   options.params.groupCk - group context for the query
   * @param {Number}   options.params.subgroupCk - subgroup context for the query
   */
  getInvoicePaymentHistory = options => {
    console.log('getInvoicePaymentHistory', options);
    return this.post(
      '/api/v1/private/payments/invoicepaymenthistory',
      options.params
    );
  };

  /**
   * Fetches invoices for the group/subgroup in context with additional supported filters
   * @param {Object}   [options={}] - configuration for the API request
   * @param {Object}   options.params - the query parameters of the request
   * @param {Number}   options.params.groupCk - group context for the query
   * @param {Number}   options.params.subgroupCk - subgroup context for the query
   * @param {Number}   options.params.status - the status with which to filter invoices by
   */
  getInvoiceSearch = (options = {}) => {
    delete options.params.parentGroupName;
    delete options.params.parentGroupId;
    delete options.params.groupName;
    delete options.params.groupId;
    delete options.params.subGroupName;
    delete options.params.subGroupId;
    console.log('getInvoiceSearch', options);
    // if(!(options.params.groupCk && options.params.subGroupCk))
    //   throw new Error('You must provide both a groupCk and a subGroupCk')
    return this.post('/api/v1/private/invoicesbycontext', options.params);
  };

  /**
   * Block or unblock invoice/list of invoices
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   * @param {Object}          options.body - the request body
   */
  putInvoiceBlockStatus = (options = {}) => {
    console.log('putInvoiceBlockStatus', options);
    return this.put('/api/v1/private/invoices', options.body, options.params);
  };

  /**
   * Fetches the available payment method(s) for the group/subgroup in context
   * @param {Object}   [options={}] - configuration for the API request
   * @param {Number}   options.groupCk - group context for the query
   * @param {Number}   options.subgroupCk - subgroup context for the query
   */
  getPaymentMethods = (options = {}) => {
    console.log(`getPaymentMethods`, options);
    if (!options.params.groupCk) {
      console.warn(
        `Invalid groupCk: ${options.params.groupCk}`,
        options
      );
      return;
    }
    return this.get('/api/v1/private/payments/methods', options.params);
  };

  /**
   * Fetches the available payment method(s) for the group/subgroup in context
   * @param {Object}   [options={}] - configuration for the API request
   * @param {Number}   options.groupCk - group context for the query
   * @param {Number}   options.subgroupCk - subgroup context for the query
   */
  getUserPaymentMethods = (options = {}) => {
    console.log(`getUserPaymentMethods`, options);
    return this.get('/api/v1/private/payments/user/methods', options.params);
  };

  /**
   * Retrieves the cybersource configuration details
   */
  getCybersourceDetails = ({ lobId, key }) => {
    if (!lobId) {
      throw new Error('No lobId provided to getCybersourceDetails');
    }
    return this.get('/api/v1/private/configurations', {
      category: `CSCONFIG_TOKEN_${lobId}`,
      key
    });
  };

  /**
   * Retrieves auto pay details
   */
  getAutoPayDetails = () => {
    return this.get('/api/v1/private/configurations', {
      category: 'Finance',
      key: 'Autopay'
    });
  };

  /**
   * Retrieves the cybersource configuration details for onetime payments
   */
  getCybersourceOnetimeDetails = ({ lobId, key }) => {
    if (!lobId) {
      throw new Error('No lobId provided to getCybersourceOnetimeDetails');
    }
    return this.get('/api/v1/private/configurations', {
      category: `CSCONFIG_ONETIME_${lobId}`,
      key
    });
  };

  /**
   * Retrieves the payment lobIds
   */
  getPaymentLobs = () => {
    return this.get('/api/v1/private/configurations', {
      category: 'INVOICE',
      key: 'PAYMENT_LOBS'
    });
  };

  /**
   * Fetches payment history for the group/subgroup in context with additional supported filters
   * @param {Object}   [options={}] - configuration for the API request
   */
  getSingleInvoice = options => {
    console.log('getSingleInvoice', options);
    return this.post(`/api/v1/private/invoices/detail`,options);
  };

  /**
   * POSTs the payment details to the API service for payments made in the app
   * @param {Object}    [options={}] - configuration for the API request
   * @param {Object}    options.body - the query parameters of the request
   * @param {Number}    options.body.totalAmount - the total amount of the invoice being paid
   * @param {String}    options.body.lineOfBusiness- the line of business for the payment
   * @param {Number}    options.body.paymentMethodId - the payment method to be used
   * @param {Object[]}  options.body.payment - the invoices to be paid
   * @param {Number}    options.body.payment.groupCk - the group the invoice belongs to
   * @param {Number}    options.body.payment.subgroupCk - the subgroup the invoice belongs to
   * @param {Number}    options.body.payment.transactionId - the id of the transaction
   * @param {Number}    options.body.payment.paymentMethodId - the id of the payment method to be used
   * @param {Number}    options.body.payment.invoiceNumber - the invoice to be paid
   * @param {Number}    options.body.payment.paidAmount - the amount to be paid on the invoice
   */
  postPayment = options => {
    console.log('postPayment', options);
    return this.post(
      '/api/v1/private/payments/transactions',
      options.body,
      options.params
    );
  };

  getSubgroupDetails = options => {
    console.log('getSubgroupDetails', options);
    if (!(options.groupCk && options.subGroupCk)) {
      throw new Error('You must provide both a groupCk and a subGroupCk');
    }
    return this.post(
      `/api/v1/private/groups/subgroups/`,
      options
    );
  };

  getSubGroupCashOnAccounts = options => {
    console.log('getSubGroupCashOnAccounts', options);
    return this.get('/api/v1/private/groups/cashOnAccounts', options.params);
  };

  configureRecurringPayments = options => {
    console.log('configureRecurringPayments', options);
    return this.put(
      '/api/v1/private/payments/methods',
      options.body,
      options.params
    );
  };

  deletePaymentMethod = options => {
    console.log('deletePaymentMethod', options);
    let paymentMethodId = options.paymentMethodId;

    if (!paymentMethodId) {
      throw new RequestParamsMissingError(
        `Invalid paymentMethodId: ${paymentMethodId}`,
        options
      );
    } else
      return this.delete(`/api/v1/private/payments/methods/${paymentMethodId}`);
  };

  deleteUserPaymentMethod = options => {
    console.log('deleteUserPaymentMethod', options);
    let paymentMethodId = options.paymentMethodId;

    if (!paymentMethodId) {
      throw new RequestParamsMissingError(
        `Invalid paymentMethodId: ${paymentMethodId}`,
        options
      );
    } else
      return this.delete(
        `/api/v1/private/payments/user/methods/${paymentMethodId}`
      );
  };

  updateDefaultUserPaymentMethod = options => {
    console.log('updateDefaultUserPaymentMethod', options);
    let paymentMethodId = options.paymentMethodId;

    if (!paymentMethodId) {
      throw new RequestParamsMissingError(
        `Invalid paymentMethodId: ${paymentMethodId}`,
        options
      );
    } else
      return this.put(
        `/api/v1/private/payments/user/methods/${paymentMethodId}`
      );
  };

  getPaymentHistoryConfiguration = options => {
    console.log('getPaymentHistoryConfiguration', options);
    if (!options.portalContext) {
      throw new Error('No portal context specified.');
    } else {
      return this.get(
        '/api/v1/private/configurations',
        // category=PAYMENT_HISTORY_GRP&key=PAYMENTSUBGRP_69655
        {
          category: 'PAYMENT_HISTORY_GRP',
          key: `PAYMENTSUBGRP_${options.portalContext.subGroupCk}`
        }
      );
    }
  };
}
